<template>
    <DividerTitle :title="title" />
    <v-row class="d-flex justify-start" v-auto-animate>
        <v-col cols="12" sm="3" md="2" lg="2">
            <v-text-field
                data-cy="info-weight"
                type="number"
                ref="weight"
                v-model="info.weight"
                :label="$t('formFields.weight')"
                suffix="t"
                v-auto-animate
                hide-details="auto"
                :rules="[requiredRule]"
            />
        </v-col>
        <v-col cols="12" sm="3" md="2" lg="2">
            <v-text-field
                type="number"
                ref="volume"
                v-model="info.volume"
                :label="$t('formFields.volume')"
                suffix="m³"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>
        <v-col cols="12" sm="3" md="2" lg="2">
            <v-text-field
                data-cy="info-mdp"
                type="number"
                ref="mdp"
                v-model="info.mdp"
                :label="$t('formFields.mdp')"
                suffix="m"
                v-auto-animate
                hide-details="auto"
                :rules="[requiredRule]"
            />
        </v-col>
        <v-col cols="12" sm="3" md="2" lg="1" v-if="formType === 'ware'">
            <v-text-field
                type="number"
                ref="price"
                v-model="info.price"
                :label="$t('formFields.price')"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>
        <v-col cols="12" sm="3" md="2" lg="1"  v-if="formType === 'truck'">
            <v-text-field
                type="number"
                ref="price"
                v-model="info.price"
                :label="$t('formFields.pricePerKm')"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>

        <!-- Currency Select -->
        <v-col cols="12" sm="4" md="2" lg="1" v-if="info.price">
            <v-select
                ref="currency"
                v-model="info.currency"
                :items="['EUR', 'RON']"
                :label="$t('formFields.currency')"
                :rules="currencyRules"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>

        <!-- VAT Checkbox -->
        <v-col cols="12" sm="2" md="2" lg="1" v-if="info.price">
            <v-checkbox
                v-model="info.vatType"
                :label="vatLabel"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>

        <v-col cols="12" sm="4" md="2" lg="2">
            <v-select
                v-model="info.paymentTerm"
                :items="paymentTerms"
                item-title="text"
                item-value="value"
                :label="$t('formFields.paymentDeadline')"
                v-auto-animate
                hide-details="auto"
            />
        </v-col>
    </v-row>
</template>

<script>
import { PAYMENT_DEADLINE } from "@/enums/truckEnums";
import DividerTitle from "./DividerTitle.vue";

export default {
    components: {
        DividerTitle,
    },
    /**
     * Component properties
     * @property {Object} modelValue - The v-model object containing pricing and measurement information.
     */
    props: {
        modelValue: {
            type: Object,
            default: () => ({
                weight: "",
                volume: "",
                mdp: "",
                price: "",
                paymentTerm: 1,
                currency: "EUR",
                vatType: false,
            }),
        },
        formType: String,
        title: String,
    },
    /**
     * Events emitted by the component
     * @event update:modelValue - Emits updated info object.
     */
    emits: ["update:modelValue"],

    data() {
        return {};
    },
    computed: {
        info: {
            get() {
                return this.modelValue;
            },
            set(newVal) {
                this.$emit("update:modelValue", newVal);
            },
        },
        paymentTerms() {
            return PAYMENT_DEADLINE.map((term) => ({
                ...term,
                text: `${term.text} ${this.$t("formFields.days")}`,
            }));
        },
        requiredRule() {
            return (value) => !!value || this.$t("validation.required");
        },
        currencyRules() {
            // If a price is set, currency must be explicitly chosen.
            return [
                (v) => {
                    if (!this.info?.price) return true;
                    return !!String(v || "").trim() || this.$t("validation.required");
                },
            ];
        },
        vatLabel() {
            return this.info?.vatType === true
                ? this.$t("formFields.withVAT")
                : this.$t("formFields.withoutVAT");
        },
    },
    methods: {
        /**
         * Manually triggers validation on all fields.
         * Returns true if all fields are valid, false otherwise.
         */
        async validateFields() {
            await this.$nextTick(); // Ensure reactivity updates before validation

            let isValid = true;

            if (this.$refs.weight) {
                const valid = await this.$refs.weight.validate();

                if (this.$refs.weight.errorMessages.length > 0) isValid = false;
            }

            if (this.$refs.mdp) {
                const valid = await this.$refs.mdp.validate();

                if (this.$refs.mdp.errorMessages.length > 0) isValid = false;
            }

            if (this.info?.price && this.$refs.currency) {
                await this.$refs.currency.validate();
                if (this.$refs.currency.errorMessages.length > 0) isValid = false;
            }

            // if (this.$refs.price) {
            //   const valid = await this.$refs.price.validate();
            //
            //   if (this.$refs.price.errorMessages.length > 0) isValid = false;
            // }

            return isValid; // Return final validation status
        },

        /**
         * Resets validation errors for all form fields.
         * This method checks if each field exists using `$refs` and calls `resetValidation()`
         * to clear any validation errors on the respective field components.
         */
        resetValidation() {
            if (this.$refs.weight) this.$refs.weight.resetValidation();
            if (this.$refs.mdp) this.$refs.mdp.resetValidation();
            if (this.$refs.price) this.$refs.price.resetValidation();
        },
        /**
         * Resets fields to their default empty values.
         */
        /**
         * Resets fields to their default empty values.
         */
        resetFields() {
            const defaults = {
                weight: "",
                volume: "",
                mdp: "",
                price: "",
                paymentTerm: 1,
                currency: "EUR",
                vatType: false,
            };
            this.$emit("update:modelValue", defaults);
        },
    },
    watch: {
        "$i18n.locale"() {
            // When the language changes, manually force validation updates
            this.resetValidation();
        },
    },
};
</script>